import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EMPTY, Subject, finalize } from 'rxjs';
import { CameraComponent } from 'src/app/shared/camera/camera.component';
import { CarouselDocumentsComponent } from 'src/app/shared/carousel-documents/carousel-documents.component';
import { ConfirmationComponent } from 'src/app/shared/confirmation/confirmation.component';
import { DetailDocument, DocumentCategory, HelpImageUrl } from 'src/app/shared/interfaces/data-selects';
import { MockEvent, PhotoData } from 'src/app/shared/interfaces/dialog-configuration.interface';
import { DocumentUploadBody } from 'src/app/shared/interfaces/step-data';
import { RequestInformationComponent } from 'src/app/shared/request-information/request-information.component';
import { DeleteDataService } from 'src/app/shared/services/delete-data.service';
import { GetDataService } from 'src/app/shared/services/get-data.service';
import { PostDataService } from 'src/app/shared/services/post-data.service';
import { PutDataService } from 'src/app/shared/services/put-data.service';
import { RequestFormService } from 'src/app/shared/services/request-form.service';
import { AutoUnsubscribeComponent } from 'src/app/shared/utils/auto-unsubscribe.component';

interface DocumentSection {
  id: number;
  name: string;
  documents: Document[];
  helpImageUrls: HelpImageUrl[];
}

interface Document {
  id: number;
  name: string;
  inputName: string;
  data?: DetailDocument;
  uploaded?: boolean;
  fileName?: string;
  canOpenCamera?: boolean;
  scanned?: boolean;
}

@Component({
  selector: 'app-step-documentos-lto-nuevo',
  templateUrl: './step-documentos-lto-nuevo.component.html',
  styleUrls: ['./step-documentos-lto-nuevo.component.scss'],
})
export class StepDocumentosLtoNuevoComponent extends AutoUnsubscribeComponent implements OnInit {
  @ViewChild(CameraComponent) cameraComponent!: CameraComponent;

  private DOMICILE_ID = '51135ea9-032f-489f-b644-c6b49132e266';

  public documentsCategory: DocumentCategory[] = [];
  protected _onDestroy = new Subject<void>();
  public cameraMovil!: boolean;
  public isWaitingApi = false;
  public disableButtonSend = false;

  cameraOpen: boolean = false;
  randomizedDocumentUploadMode: number = -1;
  selectedDocument?: Document;

  internalScannedDocuments: number[] = []; // IDs of the documents that have been scanned internally. Just to keep track in the UI.
  currentDocumentId: number = -1;
  newDomicileDocument = false;

  hasIne = true;
  ineDomicileComplete = false;
  showDomicileInput = false;
  domicileInputValue = '';
  domicileNumberInputValue = '';
  domicileInternalNumberInputValue = '';

  private originalSections: DocumentSection[] = [];
  public filteredSections: DocumentSection[] = [];

  private allowedFileTypes = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/x-compressed',
  ];

  constructor(
    private postDataService: PostDataService,
    private dialog: MatDialog,
    private requestFormService: RequestFormService,
    private getDataService: GetDataService,
    private deleteDataService: DeleteDataService,
    private router: Router,
    private putDataService: PutDataService
  ) {
    super();
  }

  private documentPrincipales!: DetailDocument[];

  public documentsSections: DocumentSection[] = [
    {
      id: 1,
      name: 'INE',
      documents: [
        { id: 1, name: 'INE FRENTE', inputName: 'Frente' },
        { id: 2, name: 'INE REVERSO', inputName: 'Reverso' },
      ],
      helpImageUrls: [],
    },
    {
      id: 2,
      name: 'Licencia de Conducir',
      documents: [
        { id: 3, name: 'LICENCIA FRENTE', inputName: 'Frente' },
        { id: 4, name: 'LICENCIA REVERSO', inputName: 'Reverso' },
      ],
      helpImageUrls: [],
    },
    {
      id: 3,
      name: 'Comprobante de domicilio',
      documents: [
        { id: 5, name: 'COMPROBANTE DE DOMICILIO', inputName: 'Con dirección visible' },
      ],
      helpImageUrls: [],
    },
  ];

  ngOnInit(): void {
    this.randomizedDocumentUploadMode = this.getRandomizedNumber();
    this.cameraMovil = /Android|iPhone/i.test(navigator.userAgent);

    const searchForm = this.requestFormService.getUpdateForm();
    const docTypes = searchForm ? searchForm.documents : [];

    this.getDataService.getApplicationsSelectsLto('MX').subscribe((resp) => {
        // Fill all doc.data, helpImageUrls, uploaded, etc…
        this.documentPrincipales =
          resp.documentCategories.find(op => op.name === 'PRINCIPALES')?.documents ?? [];

        this.documentsSections.forEach(docSection => {
          docSection.helpImageUrls = docSection.documents.flatMap(doc => {
            doc.data = this.documentPrincipales.find(d => d.name === doc.name);
            if (!doc.data) return [];
            const docType = docTypes.find(dt => dt.typeId === doc.data!.id);
            if (docType) {
              doc.uploaded = true;
              doc.fileName = docType.fileName;
              doc.scanned = docType.scanned;
            }
            return doc.data!.helpImageUrl;
          });
        });

        // Now that all doc.data exist, I save the "original" copy.
        this.originalSections = JSON.parse(JSON.stringify(this.documentsSections));

        // Finally filter by INE and domicile.
        this.filterByIneDomicile();
      });
  }

  setDomicileInputValue(value: boolean): void {
    this.domicileInputValue = '';
    this.showDomicileInput = value;
  }
  filterByIneDomicile(): void {
    // Clone "immutable" of the original definition.
    const secs: DocumentSection[] = JSON.parse(JSON.stringify(this.originalSections));

    if (this.hasIne) {
      // Show ONLY INE (we leave only FRONT).
      const ineSec = secs.find(s => s.name === 'INE');
      if (ineSec) {
        ineSec.documents = ineSec.documents.filter(d => d.name === 'INE FRENTE');
        this.filteredSections = [ineSec];
      } else {
        this.filteredSections = [];
      }

      // If doesn't have complete domicile in the INE → ask for manual domicile fields.
      this.setDomicileInputValue(!this.ineDomicileComplete);
      return;
    }

    // No INE → ask for Driver License (only FRONT) + Proof of Address.
    const withoutIne = secs.filter(s => s.name !== 'INE');
    const licSec = withoutIne.find(s => s.name === 'Licencia de Conducir');
    if (licSec) {
      licSec.documents = licSec.documents.filter(d => d.name === 'LICENCIA FRENTE');
    }
    this.filteredSections = withoutIne;
    this.setDomicileInputValue(false); // Don't show manual inputs in this path.
  }

  loadDocument(event: any, document: DetailDocument, loadMethod: string, currentDocumentId: number) {
    if (document.name === 'Comprobante de domicilio') {
      this.newDomicileDocument = true;
    }
    const fileType = event.target.files[0].type;
    const fileMimeType = event.target.files[0].size < 5000000;

    if (this.allowedFileTypes.includes(fileType) && fileMimeType) {
      const formData = new FormData();
      formData.append('file', event.target.files[0]);
      document.spinner = true;

      const postSaveDocumentLto$ = this.postDataService
        .postSaveDocumentLto(
          this.getCountry().toUpperCase(),
          this.getApplicationId(),
          document.id,
          formData,
          loadMethod
        )
        .pipe(finalize(() => { document.spinner = false; }))
        .subscribe({
          next: (resp) => {
            this.documentPrincipales.forEach((d) => {
              if (d.name == document.name) {
                d.spinner = false;
                d.uploaded = true;
                d.fileName = resp.fileName;
                d.scanned = true;
              }
            });
            document.uploaded = true;
            document.fileName = resp.fileName;
            document.scanned = true;
            this.internalScannedDocuments.push(currentDocumentId);
            this.currentDocumentId = -1;
          },
          error: (err) => {
            if (err.status === 400 && err.error.message === "You can't make another try") {
              document.uploaded = true;
            }
          },
        });
      this.subscriptions.push(postSaveDocumentLto$);
    } else {
      this.errorFormatDialog();
    }
  }

  getRandomizedNumber(): number {
    return Math.floor(Math.random() * 2);
  }

  viewDocuments(doc: string) {
    const getViewDocumentsLto$ = this.getDataService
      .getViewDocumentsLto(
        this.getCountry().toUpperCase(),
        this.getApplicationId(),
        doc
      )
      .subscribe((resp) => {
        const fileURL = URL.createObjectURL(resp);
        window.open(fileURL);
      });
    this.subscriptions.push(getViewDocumentsLto$);
  }

  openViewModelDocuments(data?: DocumentSection) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      question: data?.name != 'Comprobante de domicilio' ? '' : 'Se permiten recibos de cualquier servicio menos el de Agua',
      documents: data?.helpImageUrls
    };

    if (data?.name != 'Comprobante de domicilio') {
      this.dialog
        .open(CarouselDocumentsComponent, dialogConfig)
        .afterClosed()
        .subscribe(() => []);
    } else {
      this.dialog
        .open(ConfirmationComponent, dialogConfig)
        .afterClosed()
        .subscribe(() => []);
    }
  }

  confirmDeleteDocuments(fileName: string, doc: DetailDocument) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      title: 'Eliminar',
      question: `¿Desea eliminar el documento : ${doc.name}?`,
    };
    this.dialog
      .open(ConfirmationComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const deleteDocumentsLto$ = this.deleteDataService
            .deleteDocumentsLto(
              this.getCountry().toUpperCase(),
              this.getApplicationId(),
              this.newDomicileDocument ? doc.fileName : fileName
            )
            .subscribe({
              next: () => {
                this.successErrorDeleteDocumentDialog();
                doc.uploaded = false;

                if (doc.id === this.DOMICILE_ID) {
                  const section = this.documentsSections.find((section) => section.id === 3); // Comprobante de domicilio.

                  if (section) {
                    const document = section.documents.find((d) => d.id === 5); // Comprobante de domicilio.
                    if (document) {
                      document.uploaded = false;
                      document.scanned = false;
                      document.fileName = undefined;
                      this.internalScannedDocuments = this.internalScannedDocuments.filter((id) => id !== document.id);
                    }
                  }
                }
              },
              error: (err) => {
                this.successErrorDeleteDocumentDialog(err.status);
              },
            });
          this.subscriptions.push(deleteDocumentsLto$);
        }
      });
  }

  successErrorDeleteDocumentDialog(error?: number) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      title: error ? 'Error!' : 'Éxito!',
      question: error
        ? `Ha ocurrido un error, no se pudo eliminar el documento.`
        : `El documento se eliminó correctamente. `,
    };
    this.dialog
      .open(ConfirmationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => EMPTY);
  }

  openCamera(selectedDocument: Document) {
    this.currentDocumentId = selectedDocument.id;
    let messagePosition = '';
    let messageDocument = '';

    if (this.cameraComponent) {
      this.selectedDocument = selectedDocument;

      // Set the message text for the camera component.
      if (selectedDocument.id === 1 || selectedDocument.id === 3) {
        messagePosition = 'frente';
      } else if (selectedDocument.id === 2 || selectedDocument.id === 4) {
        messagePosition = 'reverso';
      } else {
        messagePosition = '';
        messageDocument = 'Comprobante De Domicilio';
      }
      const messageText = `Foto ${messagePosition ? ('del ' + messagePosition) : ''} de tu ${messageDocument ? messageDocument : selectedDocument.name.split(' ')[0].toUpperCase()}.`;

      // Open the camera component.
      this.cameraComponent.openCamera(messageText);
      this.cameraOpen = true;
    }
  }

  confirmPhoto(photoData: PhotoData) {
    const mockEvent: MockEvent = photoData.mockEvent;
    // Use the loadDocument method to upload the file.
    const documentData: DetailDocument | undefined = this.documentsSections
      .flatMap(section => section.documents)
      .find(doc => doc.id === this.selectedDocument?.id)?.data;

    if (documentData) {
      this.loadDocument(mockEvent, documentData, 'CUSTOM_CAMERA', this.currentDocumentId);
    }
  }

  errorFormatDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      title: 'Error de Formato.',
      question: `Solo puede agregar documentos con los siguientes formatos: .PDF, .JPG, .PNG y con un tamaño menor a 5MB. `,
    };
    this.dialog
      .open(ConfirmationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => []);
  }

  buttonSendDocument() {
    this.isWaitingApi = true;
    const body: DocumentUploadBody = {
      street: this.domicileInputValue,
      externalNumber: this.domicileNumberInputValue,
      internalNumber: this.domicileInternalNumberInputValue,
      hasIne: this.hasIne,
    }
    const putUpdateRequestMainDocuments$ = this.putDataService.putUpdateRequestMainDocuments(this.getCountry().toUpperCase(), this.getApplicationId(), body)
      .subscribe({
        next: (resp) => {
          console.log("OK: sendDocument")
          this.router.navigate(['/confirmacion-solicitud-2']);
          console.log("OK redirect: sendDocument")
        },
        error: (err) => {
          console.error("erro: sendDocument", err)
          this.isWaitingApi = false;
          this.openRequestInformationDialog(err);
        },
      });
    this.subscriptions.push(putUpdateRequestMainDocuments$);
  }

  openRequestInformationDialog(event: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error.';

    const errorCoderDocuments = `MISSING_DOCUMENTS`.match(
      event?.error.errors[0].code
    );

    const missingDocumentsMessage = event?.error.errors[0].message;
    if (errorCoderDocuments) {
      errorMessage = `Debe cargar todos los documentos obligatorios (*) requeridos para poder continuar.
        \n${missingDocumentsMessage} `;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Información de solicitud',
      question: errorMessage,
    };

    this.dialog
      .open(RequestInformationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => []);
  }

  getCountry() {
    return "MX";
    // return !this.requestFormService.getUpdateForm()
    //   ? sessionStorage.getItem('language')!
    //   : this.requestFormService.getUpdateForm().countryCode.code.toLowerCase();
  }

  getApplicationId() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('applicationId')!
      : this.requestFormService.getUpdateForm().id;
  }
}
